import { WebsiteConfig } from "@/types/WebsiteConfig";
import { apiDelete, apiGet, apiGetRaw, apiPut, apiPost, apiPostFormData } from "../_apiShim";
import { Page } from "@/types/Page";
import { CertificateResult } from "@/types/CertificateResult";
import { updatePage } from "./Pages";
import { withSourceEventId } from "@/lib/ai/aiContentQuery";

const BASE_PATH = "/api/v1/website-config/";

/**
 * Event website favicon — stored at a permanent public-assets URL and served
 * for every site type (AI / ZIP / builder) via the worker's /favicon.ico
 * redirect. Upload a 512×512 PNG; change anytime, no re-publish.
 */
export async function uploadWebsiteFavicon(eventId: string, file: File): Promise<string> {
  if (!file.type.includes("png")) throw new Error("Favicon must be a PNG image");
  const formData = new FormData();
  formData.append("favicon", file);
  const data = await apiPostFormData<string>(
    `${BASE_PATH}${eventId}/favicon`,
    formData,
    { "Cache-Control": "no-store" },
    { responseType: "text" },
  );
  return data.replaceAll('"', "");
}

export async function deleteWebsiteFavicon(eventId: string): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/favicon`, { "Cache-Control": "no-store" });
}

export async function getWebsiteFaviconUrl(eventId: string): Promise<string> {
  const res = await apiGet<{ faviconUrl: string }>(`${BASE_PATH}${eventId}/favicon`, {
    "Cache-Control": "no-store",
  });
  return res.faviconUrl;
}
const TEMPLATE_WEBSITE_CONFIG_PATH = "/api/v1/template/website-config/";
const TEMPLATE_PAGES_PATH = "/api/v1/template/pages/";

/**
 * Check the managed certificate status for a custom domain
 */
export async function checkCertificateStatus(
  eventId: string,
): Promise<CertificateResult> {
  return await apiGet<CertificateResult>(
    `${BASE_PATH}${eventId}/certificate-status`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Remove custom domain from the website config and Cloudflare for SaaS
 * Custom Hostname.
 */
export async function removeCustomDomain(
  eventId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/custom-domain`, {
    "Cache-Control": "no-store",
  });
}

// CFCH cutover: verifyCnameStatus removed. Cloudflare for SaaS auto-
// validates HTTP DCV — checkCertificateStatus is the single polling
// entry point now.

/**
 * Get or create a certificate for a custom domain.
 */
export async function getCustomDomainMetaData(
  eventId: string,
  domain?: string,
): Promise<CertificateResult> {
  return await apiPost<CertificateResult>(
    `${BASE_PATH}${eventId}/custom-domain-metadata`,
    { domain: domain || null },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Create a website config for an event
 */
export async function createWebsiteConfig(
  eventId: string,
  websiteConfig: WebsiteConfig,
  pages: Page[] = [],
  /** Art. 50 KI-VO: the AI tool that produced this site (server stamps
   *  `website_configs.aiGenerated`); omitted for a human create. */
  aiContentTool?: string,
): Promise<string> {
  const response = await apiPost<string>(
    `${BASE_PATH}${eventId}`,
    {
      bodyFont: websiteConfig.bodyFont,
      headerFont: websiteConfig.headerFont,
      primaryColor: websiteConfig.primaryColor,
      secondaryColor: websiteConfig.secondaryColor,
      backgroundColor: websiteConfig.backgroundColor,
      rounding: websiteConfig.rounding,
      appBar: websiteConfig.appBar,
      footer: websiteConfig.footer,
      homeSlug: websiteConfig.homeSlug,
      primaryLanguage: websiteConfig.primaryLanguage,
      languages: websiteConfig.languages,
      published: websiteConfig.published,
      favIcon: websiteConfig.favIcon,
      touchIcon: websiteConfig.touchIcon,
      colorCookieHeader: websiteConfig.colorCookieHeader,
      fontCookieHeader: websiteConfig.fontCookieHeader,
      colorCookieBody: websiteConfig.colorCookieBody,
      fontCookieBody: websiteConfig.fontCookieBody,
      backgroundCookieColor: websiteConfig.backgroundCookieColor,
      cookieHeaderText: websiteConfig.cookieHeaderText,
      cookieBodyText: websiteConfig.cookieBodyText,
      cookieEnabled: websiteConfig.cookieEnabled,
      cookiePolicyUrl: websiteConfig.cookiePolicyUrl,
      imprintUrl: websiteConfig.imprintUrl,
      privacyPolicyUrl: websiteConfig.privacyPolicyUrl,
      defaultLoginJson: websiteConfig.defaultLoginJson,
      defaultRegisterJson: websiteConfig.defaultRegisterJson,
      customCss: websiteConfig.customCss,
      bodyFontColor: websiteConfig.bodyFontColor,
      bodyFontSize: websiteConfig.bodyFontSize,
      bodyLayoutOption: websiteConfig.bodyLayoutOption,
      h1FontFamily: websiteConfig.h1FontFamily,
      h1FontSize: websiteConfig.h1FontSize,
      h1FontColor: websiteConfig.h1FontColor,
      h2FontFamily: websiteConfig.h2FontFamily,
      h2FontSize: websiteConfig.h2FontSize,
      h2FontColor: websiteConfig.h2FontColor,
      h3FontFamily: websiteConfig.h3FontFamily,
      h3FontSize: websiteConfig.h3FontSize,
      h3FontColor: websiteConfig.h3FontColor,
      primaryBtnBgColor: websiteConfig.primaryBtnBgColor,
      primaryBtnBgHoverColor: websiteConfig.primaryBtnBgHoverColor,
      primaryBtnBorderColor: websiteConfig.primaryBtnBorderColor,
      primaryBtnBorderHoverColor: websiteConfig.primaryBtnBorderHoverColor,
      primaryBtnFontColor: websiteConfig.primaryBtnFontColor,
      primaryBtnFontHoverColor: websiteConfig.primaryBtnFontHoverColor,
      primaryBtnFontSize: websiteConfig.primaryBtnFontSize,
      primaryBtnFontHoverSize: websiteConfig.primaryBtnFontHoverSize,
      primaryBtnBorderRadius: websiteConfig.primaryBtnBorderRadius,
      primaryBtnHoverBorderRadius: websiteConfig.primaryBtnHoverBorderRadius,
      secondaryBtnBgColor: websiteConfig.secondaryBtnBgColor,
      secondaryBtnBgHoverColor: websiteConfig.secondaryBtnBgHoverColor,
      secondaryBtnBorderColor: websiteConfig.secondaryBtnBorderColor,
      secondaryBtnBorderHoverColor: websiteConfig.secondaryBtnBorderHoverColor,
      secondaryBtnFontColor: websiteConfig.secondaryBtnFontColor,
      secondaryBtnFontHoverColor: websiteConfig.secondaryBtnFontHoverColor,
      secondaryBtnFontSize: websiteConfig.secondaryBtnFontSize,
      secondaryBtnFontHoverSize: websiteConfig.secondaryBtnFontHoverSize,
      secondaryBtnBorderRadius: websiteConfig.secondaryBtnBorderRadius,
      secondaryBtnHoverBorderRadius:
        websiteConfig.secondaryBtnHoverBorderRadius,
      hyperlinkFontColor: websiteConfig.hyperlinkFontColor,
      hyperlinkFontHoverColor: websiteConfig.hyperlinkFontHoverColor,
      hyperlinkUnderline: websiteConfig.hyperlinkUnderline,
      formInputLabelFontColor: websiteConfig.formInputLabelFontColor,
      formInputInputFontColor: websiteConfig.formInputInputFontColor,
      formInputBackgroundColor: websiteConfig.formInputBackgroundColor,
      formInputBorder: websiteConfig.formInputBorder,
      formInputBorderColor: websiteConfig.formInputBorderColor,
      formInputBorderRadius: websiteConfig.formInputBorderRadius,
      formInputBorderStyle: websiteConfig.formInputBorderStyle,
      formInputBorderWidth: websiteConfig.formInputBorderWidth,
      formInputBorderSides: websiteConfig.formInputBorderSides,
      formInputFontFamily: websiteConfig.formInputFontFamily,
      formInputFontSize: websiteConfig.formInputFontSize,
      formInputErrorColor: websiteConfig.formInputErrorColor,
      openGraphTitle: websiteConfig.openGraphTitle,
      openGraphDescription: websiteConfig.openGraphDescription,
      openGraphImageUrl: websiteConfig.openGraphImageUrl,
      senderEmail: websiteConfig.senderEmail,
      loginAfterRegister: websiteConfig.loginAfterRegister,
      notifyEmail: websiteConfig.notifyEmail,
      pages: pages.map((page) => ({
        pageName: page.pageName,
        slug: page.slug,
        active: page.active,
        websiteJson: page.websiteJson,
      })),
      ...(aiContentTool ? { aiContentTool } : {}),
    },
    {
      "Cache-Control": "no-store",
    },
  );

  if (typeof response === "string") {
    return response.replace(/"/g, "").replace(/[\[\]]/g, "");
  }

  return "";
}

/**
 * Get the website config for an event.
 *
 * Returns `null` when no `website_configs` document exists for the event
 * (the backend post-stage-A returns 200 + empty body for that case so the
 * "missing config" state is no longer a 404). axios parses an empty body
 * as `""`, not `null` — the coercion below normalises both shapes so the
 * caller sees one canonical `null` sentinel.
 *
 * Pre-stage-A backend still 404s for missing docs; that path surfaces as
 * a ServerError further up in the handler — callers handle both null and
 * ServerError so deploy ordering is safe in either direction.
 */
export async function getWebsiteConfig(
  eventId: string,
  fields?: string,
): Promise<WebsiteConfig | null> {
  const queryParams = fields ? `?fields=${encodeURIComponent(fields)}` : "";
  const data = await apiGet<WebsiteConfig | "" | null>(
    `${BASE_PATH}${eventId}${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  return data === "" || data == null ? null : data;
}

/**
 * Check if the website is published and the CloudFront distribution is deployed
 */
export async function isWebsitePublished(
  eventId: string,
): Promise<boolean> {
  return await apiGet<boolean>(
    `${BASE_PATH}${eventId}/is-published`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export interface WebsiteHealthResult {
  /** Public URL responded with 200 to a fresh fetch (DNS + TLS + worker
   *  + bootstrap all green). Drives the green/yellow indicator. */
  live: boolean;
  /** WebsiteConfig.published flag — admin intent to be live. */
  published: boolean;
  status: number | null;
  latencyMs: number;
  ts: string;
  /**
   * 2026-05-16 (audit P2.14): true when the response came from a
   * fallback path (missing env var, worker unreachable, fetch error),
   * NOT a successful direct probe. Cockpit should render amber and
   * keep polling — without this, a worker outage made `live=published`
   * fall through silently and stop polling, masking the outage.
   */
  degraded?: boolean;
}

/**
 * Probe the public URL via the website-builder worker's tenant-agnostic
 * admin endpoint. The worker self-fetches `<eventId>.veltric.app` and
 * caches the result for 10s (Redis), so polling at 5s costs at most
 * one real probe per minute per event.
 *
 * Falls back to a `live === published` shape in dev when the env var
 * isn't set — keeps the indicator usable locally without standing up
 * the worker.
 */
export async function getWebsiteHealth(
  eventId: string,
): Promise<WebsiteHealthResult> {
  const baseUrl = (
    import.meta.env.VITE_WEBSITE_BUILDER_BASE_URL as string | undefined
  )?.trim();
  // 2026-05-16 (audit P2.14): on every fallback path, return
  // `degraded: true` AND console.warn so a misconfigured env or worker
  // outage is observable (Sentry breadcrumb) instead of silently
  // collapsing to a false-green "live" indicator.
  const buildFallback = (reason: "env" | "http" | "network"): WebsiteHealthResult => {
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(`[website-health] degraded fallback (${reason})`);
    }
    return {
      live: false,
      published: false,
      status: null,
      latencyMs: 0,
      ts: new Date().toISOString(),
      degraded: true,
    };
  };

  if (!baseUrl) {
    // Hydrate published from the backend so the UI can at least show
    // "published / unpublished" intent. live stays false because we
    // can't actually probe.
    const published = await isWebsitePublished(eventId);
    return { ...buildFallback("env"), published };
  }
  const url = `${baseUrl.replace(/\/$/, "")}/api/admin/health?eventId=${encodeURIComponent(eventId)}`;
  try {
    const resp = await fetch(url, {
      method: "GET",
      headers: { "Cache-Control": "no-store" },
      credentials: "omit",
    });
    if (resp.ok) {
      return (await resp.json()) as WebsiteHealthResult;
    }
    // Worker not yet deployed (e.g. frontend rolled to prod before the
    // worker prod tag) → mark degraded so polling keeps trying.
    const published = await isWebsitePublished(eventId);
    return { ...buildFallback("http"), published };
  } catch {
    // CORS / network error — same fallback.
    const published = await isWebsitePublished(eventId);
    return { ...buildFallback("network"), published };
  }
}

/**
 * Toggle the website publish status (enable/disable CloudFront distribution)
 */
export async function toggleWebsiteStatus(
  eventId: string,
  published: boolean,
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/toggle-status`,
    {
      published: published,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Every WebsiteConfig field a save may carry.
 *
 * SINGLE SOURCE for both write paths. `updateWebsiteConfigAndPage` used
 * to hand-roll a 15-key body instead, so a save that carried a theme
 * change AND a canvas change persisted the canvas and silently dropped
 * the theme — and then cleared the dirty flag, making the loss invisible.
 * Add new fields HERE, never to a per-function list.
 *
 * Backend twin: `WebsiteConfigResource.toWebsiteConfig`.
 */
export const WEBSITE_CONFIG_FIELDS: (keyof WebsiteConfig)[] = [
  "bodyFont", "headerFont", "primaryColor", "secondaryColor", "backgroundColor",
  "rounding", "appBar", "footer", "homeSlug", "published", "favIcon", "touchIcon",
  "colorCookieHeader", "fontCookieHeader", "colorCookieBody", "fontCookieBody",
  "backgroundCookieColor", "cookieHeaderText", "cookieBodyText", "cookieEnabled",
  "cookiePolicyUrl", "imprintUrl", "privacyPolicyUrl", "customCss", "bodyFontColor", "bodyFontSize", "bodyLayoutOption",
  "h1FontFamily", "h1FontSize", "h1FontColor", "h2FontFamily", "h2FontSize", "h2FontColor",
  "h3FontFamily", "h3FontSize", "h3FontColor",
  "primaryBtnBgColor", "primaryBtnBgHoverColor", "primaryBtnBorderColor",
  "primaryBtnBorderHoverColor", "primaryBtnFontColor", "primaryBtnFontHoverColor",
  "primaryBtnFontSize", "primaryBtnFontHoverSize", "primaryBtnBorderRadius",
  "primaryBtnHoverBorderRadius",
  "secondaryBtnBgColor", "secondaryBtnBgHoverColor", "secondaryBtnBorderColor",
  "secondaryBtnBorderHoverColor", "secondaryBtnFontColor", "secondaryBtnFontHoverColor",
  "secondaryBtnFontSize", "secondaryBtnFontHoverSize", "secondaryBtnBorderRadius",
  "secondaryBtnHoverBorderRadius",
  "hyperlinkFontColor", "hyperlinkFontHoverColor", "hyperlinkUnderline",
  "formInputLabelFontColor", "formInputInputFontColor", "formInputBackgroundColor",
  "formInputBorder", "formInputBorderColor", "formInputBorderRadius",
  "formInputBorderStyle", "formInputBorderWidth", "formInputBorderSides",
  "formInputFontFamily", "formInputFontSize", "formInputErrorColor",
  "openGraphTitle", "openGraphDescription", "openGraphImageUrl",
  "senderEmail", "loginAfterRegister", "notifyEmail",
  "availableFrom", "availableUntil",
  // Search-engine opt-out (B-6). Audit fix: missing here = payload.noindex
  // silently stripped from every PUT.
  "noindex",
  "loginBlockEnabled", "loginBlockConditions", "loginBlockMessage",
  "loginBlockThresholdEnabled", "loginBlockThresholdCount",
  "loginBlockThresholdConditions", "loginBlockThresholdStatuses",
  "registrationWindows", "resubmitDeadline",
  "resubmitBlockEnabled", "resubmitBlockMessage", "resubmitBlockConditions",
  "dedupeEnabled", "dedupeConditions",
  "primaryLanguage", "languages",
];

/**
 * Update a website config.
 *
 * 2026-05-16 (audit CRIT-6): optional `expectedVersion` sent as the
 * `If-Match` header. Backend rejects with 409 `versionMismatch` if
 * another tab raced ahead; otherwise the row's version is bumped via
 * Mongo $inc atomically with the update. Cockpit should read the
 * loaded `WebsiteConfig.version` from a prior GET and pass it here
 * for every user-driven save. Omitting falls through to the legacy
 * unguarded path so existing code paths aren't broken during rollout.
 */
export async function updateWebsiteConfig(
  eventId: string,
  websiteConfig: WebsiteConfig,
  expectedVersion?: number,
): Promise<void> {
  const allFields = WEBSITE_CONFIG_FIELDS;

  const body: Record<string, unknown> = {};
  for (const key of allFields) {
    if (websiteConfig[key] !== undefined) {
      body[key] = websiteConfig[key];
    }
  }

  const headers: Record<string, string> = { "Cache-Control": "no-store" };
  if (expectedVersion !== undefined && expectedVersion !== null) {
    headers["If-Match"] = String(expectedVersion);
  }
  await apiPut<void>(
    `${BASE_PATH}${eventId}`,
    body,
    headers,
  );
}

/**
 * Delete a website config for an event
 */
export async function deleteWebsiteConfig(
  eventId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Check if a website config exists for an event
 */
export async function websiteConfigExists(
  eventId: string,
): Promise<boolean> {
  return await apiGet<boolean>(
    `${BASE_PATH}${eventId}/exists`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Get a presigned S3 URL for uploading a custom website ZIP file
 */
export async function getPresignedCustomWebsiteUploadUrl(
  eventId: string,
  fileName: string,
  fileSize: number,
): Promise<{ uploadUrl: string }> {
  return await apiPost<{ uploadUrl: string }>(
    `${BASE_PATH}${eventId}/presign-custom-website-upload`,
    { fileName, fileSize },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Notify the backend that the custom website ZIP has been uploaded to S3
 * so it can validate and extract the contents
 */
export async function processCustomWebsiteUpload(
  eventId: string,
  fileName: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/process-custom-website`,
    { fileName },
    { "Cache-Control": "no-store" },
  );
}

/**
 * Cancel a staged custom-website upload by deleting the orphan R2
 * object. Called when the subsequent process step fails — without
 * this the staged ZIP would sit in R2 forever (no lifecycle rule on
 * the prefix). Idempotent on the backend. 2026-05-16 (audit P1.7).
 */
export async function cancelCustomWebsiteUpload(
  eventId: string,
  fileName: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${eventId}/custom-website-upload?fileName=${encodeURIComponent(fileName)}`,
    { "Cache-Control": "no-store" },
  );
}

/**
 * 2026-09-28: make sure the event has a website_config row — an UNPUBLISHED
 * shell is created when there is none. The settings dialog (custom domain,
 * favicon, status, sharing) needs a row to read and write; before this it was
 * reachable only once a template or ZIP existed. Idempotent: 204 whether the
 * row was created or already there.
 */
export async function ensureWebsiteConfig(eventId: string): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/ensure`,
    undefined,
    { "Cache-Control": "no-store" },
  );
}

/**
 * Set up a custom website from scratch: create minimal config + process ZIP + activate.
 * Called after the ZIP has been uploaded to S3.
 */
export async function setupCustomWebsite(
  eventId: string,
  fileName: string,
  publishNow: boolean,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/setup-custom-website`,
    // 2026-08-08: setup activates the ZIP; taking the site PUBLIC is a
    // separate intent the operator states in the upload dialog. The backend
    // treats an absent field as "do not publish", so it is always sent
    // explicitly from here.
    { fileName, publishNow },
    { "Cache-Control": "no-store" },
  );
}

/**
 * Toggle between custom website and builder website
 */
export async function toggleCustomWebsite(
  eventId: string,
  customWebsiteActive: boolean,
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/toggle-custom-website`,
    { customWebsiteActive },
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Delete the custom website for an event
 */
export async function deleteCustomWebsite(
  eventId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/custom-website`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Download the uploaded custom website re-packaged as a ZIP blob.
 * The backend re-zips the live extracted assets — the original upload
 * ZIP is discarded after extraction.
 */
export async function downloadCustomWebsite(eventId: string): Promise<Blob> {
  const res = await apiGetRaw(`${BASE_PATH}${eventId}/custom-website-download`, {
    "Cache-Control": "no-store",
  });
  return await res.blob();
}

/**
 * Get the custom website status for an event
 */
export async function getCustomWebsiteStatus(
  eventId: string,
): Promise<{
  customWebsiteUploaded: boolean;
  customWebsiteActive: boolean;
  customWebsiteFileName: string | null;
}> {
  return await apiGet<{
    customWebsiteUploaded: boolean;
    customWebsiteActive: boolean;
    customWebsiteFileName: string | null;
  }>(
    `${BASE_PATH}${eventId}/custom-website-status`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Update both website config and a specific page for an event
 */
export async function updateWebsiteConfigAndPage(
  eventId: string,
  websiteConfig: WebsiteConfig,
  page: Page,
  expectedVersion?: number,
  /** Art. 50 KI-VO: an AI tool changed this canvas since the last save — the
   *  server keeps/stamps the site's `aiGenerated` mark. Write-only. */
  aiContentTool?: string,
): Promise<void> {
  // Same field set and same `!== undefined` filter as the plain PUT. The
  // hardcoded 15-key object this replaced is what silently ate the
  // builder's theme edits (and four consent-text fields) whenever a save
  // carried canvas changes too.
  const body: Record<string, unknown> = {};
  for (const key of WEBSITE_CONFIG_FIELDS) {
    if (websiteConfig[key] !== undefined) {
      body[key] = websiteConfig[key];
    }
  }
  body.pageId = page.pageId;
  body.websiteJson = page.websiteJson;
  if (aiContentTool) body.aiContentTool = aiContentTool;

  const headers: Record<string, string> = { "Cache-Control": "no-store" };
  // Concurrency guard, same idiom as the sibling above: the backend
  // already parses If-Match here and 409s `versionMismatch` on a stale
  // write — the builder just never sent it, so two admins in the same
  // event silently last-write-wins.
  if (expectedVersion !== undefined && expectedVersion !== null) {
    headers["If-Match"] = String(expectedVersion);
  }
  await apiPut(`${BASE_PATH}${eventId}/with-page`, body, headers);
}

// ── Template website config endpoints ─────────────────────────────────────────

/**
 * Create a template website config with pages
 */
export async function createTemplateWebsiteConfig(
  templateId: string,
  websiteConfig: WebsiteConfig,
  pages: Page[] = [],
  /**
   * Art. 50 Abs. 2 KI-VO (plan rev. 4.2 J6): a config copied out of an event
   * names that SOURCE event, so the server copies its guest-content mark onto
   * the event template. Absent ⇒ the URL is the pre-mark URL.
   */
  sourceEventId?: string,
): Promise<string> {
  const response = await apiPost<string>(
    withSourceEventId(`${TEMPLATE_WEBSITE_CONFIG_PATH}${templateId}`, sourceEventId),
    {
      registrationType: websiteConfig.registrationType,
      bodyFont: websiteConfig.bodyFont,
      headerFont: websiteConfig.headerFont,
      primaryColor: websiteConfig.primaryColor,
      secondaryColor: websiteConfig.secondaryColor,
      backgroundColor: websiteConfig.backgroundColor,
      rounding: websiteConfig.rounding,
      appBar: websiteConfig.appBar,
      footer: websiteConfig.footer,
      homeSlug: websiteConfig.homeSlug,
      primaryLanguage: websiteConfig.primaryLanguage,
      languages: websiteConfig.languages,
      published: websiteConfig.published,
      favIcon: websiteConfig.favIcon,
      touchIcon: websiteConfig.touchIcon,
      colorCookieHeader: websiteConfig.colorCookieHeader,
      fontCookieHeader: websiteConfig.fontCookieHeader,
      colorCookieBody: websiteConfig.colorCookieBody,
      fontCookieBody: websiteConfig.fontCookieBody,
      backgroundCookieColor: websiteConfig.backgroundCookieColor,
      cookieHeaderText: websiteConfig.cookieHeaderText,
      cookieBodyText: websiteConfig.cookieBodyText,
      cookieEnabled: websiteConfig.cookieEnabled,
      cookiePolicyUrl: websiteConfig.cookiePolicyUrl,
      imprintUrl: websiteConfig.imprintUrl,
      privacyPolicyUrl: websiteConfig.privacyPolicyUrl,
      defaultLoginJson: websiteConfig.defaultLoginJson,
      defaultRegisterJson: websiteConfig.defaultRegisterJson,
      customCss: websiteConfig.customCss,
      bodyFontColor: websiteConfig.bodyFontColor,
      bodyFontSize: websiteConfig.bodyFontSize,
      bodyLayoutOption: websiteConfig.bodyLayoutOption,
      h1FontFamily: websiteConfig.h1FontFamily,
      h1FontSize: websiteConfig.h1FontSize,
      h1FontColor: websiteConfig.h1FontColor,
      h2FontFamily: websiteConfig.h2FontFamily,
      h2FontSize: websiteConfig.h2FontSize,
      h2FontColor: websiteConfig.h2FontColor,
      h3FontFamily: websiteConfig.h3FontFamily,
      h3FontSize: websiteConfig.h3FontSize,
      h3FontColor: websiteConfig.h3FontColor,
      primaryBtnBgColor: websiteConfig.primaryBtnBgColor,
      primaryBtnBgHoverColor: websiteConfig.primaryBtnBgHoverColor,
      primaryBtnBorderColor: websiteConfig.primaryBtnBorderColor,
      primaryBtnBorderHoverColor: websiteConfig.primaryBtnBorderHoverColor,
      primaryBtnFontColor: websiteConfig.primaryBtnFontColor,
      primaryBtnFontHoverColor: websiteConfig.primaryBtnFontHoverColor,
      primaryBtnFontSize: websiteConfig.primaryBtnFontSize,
      primaryBtnFontHoverSize: websiteConfig.primaryBtnFontHoverSize,
      primaryBtnBorderRadius: websiteConfig.primaryBtnBorderRadius,
      primaryBtnHoverBorderRadius: websiteConfig.primaryBtnHoverBorderRadius,
      secondaryBtnBgColor: websiteConfig.secondaryBtnBgColor,
      secondaryBtnBgHoverColor: websiteConfig.secondaryBtnBgHoverColor,
      secondaryBtnBorderColor: websiteConfig.secondaryBtnBorderColor,
      secondaryBtnBorderHoverColor: websiteConfig.secondaryBtnBorderHoverColor,
      secondaryBtnFontColor: websiteConfig.secondaryBtnFontColor,
      secondaryBtnFontHoverColor: websiteConfig.secondaryBtnFontHoverColor,
      secondaryBtnFontSize: websiteConfig.secondaryBtnFontSize,
      secondaryBtnFontHoverSize: websiteConfig.secondaryBtnFontHoverSize,
      secondaryBtnBorderRadius: websiteConfig.secondaryBtnBorderRadius,
      secondaryBtnHoverBorderRadius: websiteConfig.secondaryBtnHoverBorderRadius,
      hyperlinkFontColor: websiteConfig.hyperlinkFontColor,
      hyperlinkFontHoverColor: websiteConfig.hyperlinkFontHoverColor,
      hyperlinkUnderline: websiteConfig.hyperlinkUnderline,
      formInputLabelFontColor: websiteConfig.formInputLabelFontColor,
      formInputInputFontColor: websiteConfig.formInputInputFontColor,
      formInputBackgroundColor: websiteConfig.formInputBackgroundColor,
      formInputBorder: websiteConfig.formInputBorder,
      formInputBorderColor: websiteConfig.formInputBorderColor,
      formInputBorderRadius: websiteConfig.formInputBorderRadius,
      formInputBorderStyle: websiteConfig.formInputBorderStyle,
      formInputBorderWidth: websiteConfig.formInputBorderWidth,
      formInputBorderSides: websiteConfig.formInputBorderSides,
      formInputFontFamily: websiteConfig.formInputFontFamily,
      formInputFontSize: websiteConfig.formInputFontSize,
      formInputErrorColor: websiteConfig.formInputErrorColor,
      openGraphTitle: websiteConfig.openGraphTitle,
      openGraphDescription: websiteConfig.openGraphDescription,
      openGraphImageUrl: websiteConfig.openGraphImageUrl,
      senderEmail: websiteConfig.senderEmail,
      loginAfterRegister: websiteConfig.loginAfterRegister,
      notifyEmail: websiteConfig.notifyEmail,
      pages: pages.map((page) => ({
        pageName: page.pageName,
        slug: page.slug,
        active: page.active,
        websiteJson: page.websiteJson,
      })),
    },
    {
      "Cache-Control": "no-store",
    },
  );

  if (typeof response === "string") {
    return response.replace(/"/g, "").replace(/[\[\]]/g, "");
  }

  return "";
}

/**
 * Delete the template website config
 */
export async function deleteTemplateWebsiteConfig(
  templateId: string,
): Promise<void> {
  await apiDelete<void>(`${TEMPLATE_WEBSITE_CONFIG_PATH}${templateId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Get the template website config
 */
export async function getTemplateWebsiteConfig(
  templateId: string,
): Promise<WebsiteConfig> {
  return await apiGet<WebsiteConfig>(
    `${TEMPLATE_WEBSITE_CONFIG_PATH}${templateId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Template twin of {@link WEBSITE_CONFIG_FIELDS} (adds the template-only
 * registration defaults). Shared by both template write paths for the same
 * reason — see WEBSITE_CONFIG_FIELDS.
 */
// NOTE: `registrationType`, `defaultLoginJson` and `defaultRegisterJson`
// are deliberately NOT here. They exist only on the CREATE form server-side
// and are not mapped by `toWebsiteConfig`, so sending them on an UPDATE was
// silently dropped by Yasson — an organiser editing them got no error and no
// persistence, which is the same silent-loss class this wave fixed for the
// theme fields. Listing them implied a round trip that never happened.
export const TEMPLATE_WEBSITE_CONFIG_FIELDS: (keyof WebsiteConfig)[] = [
  "bodyFont", "headerFont", "primaryColor", "secondaryColor", "backgroundColor",
  "rounding", "appBar", "footer", "homeSlug", "published", "favIcon", "touchIcon",
  "colorCookieHeader", "fontCookieHeader", "colorCookieBody", "fontCookieBody",
  "backgroundCookieColor", "cookieHeaderText", "cookieBodyText", "cookieEnabled",
  "cookiePolicyUrl", "imprintUrl", "privacyPolicyUrl", "customCss", "bodyFontColor", "bodyFontSize", "bodyLayoutOption",
  "h1FontFamily", "h1FontSize", "h1FontColor", "h2FontFamily", "h2FontSize", "h2FontColor",
  "h3FontFamily", "h3FontSize", "h3FontColor",
  "primaryBtnBgColor", "primaryBtnBgHoverColor", "primaryBtnBorderColor",
  "primaryBtnBorderHoverColor", "primaryBtnFontColor", "primaryBtnFontHoverColor",
  "primaryBtnFontSize", "primaryBtnFontHoverSize", "primaryBtnBorderRadius",
  "primaryBtnHoverBorderRadius",
  "secondaryBtnBgColor", "secondaryBtnBgHoverColor", "secondaryBtnBorderColor",
  "secondaryBtnBorderHoverColor", "secondaryBtnFontColor", "secondaryBtnFontHoverColor",
  "secondaryBtnFontSize", "secondaryBtnFontHoverSize", "secondaryBtnBorderRadius",
  "secondaryBtnHoverBorderRadius",
  "hyperlinkFontColor", "hyperlinkFontHoverColor", "hyperlinkUnderline",
  "formInputLabelFontColor", "formInputInputFontColor", "formInputBackgroundColor",
  "formInputBorder", "formInputBorderColor", "formInputBorderRadius",
  "formInputBorderStyle", "formInputBorderWidth", "formInputBorderSides",
  "formInputFontFamily", "formInputFontSize", "formInputErrorColor",
  "openGraphTitle", "openGraphDescription", "openGraphImageUrl",
  "senderEmail", "loginAfterRegister", "notifyEmail",
  "primaryLanguage", "languages",
];

/**
 * Update the template website config
 */
export async function updateTemplateWebsiteConfig(
  templateId: string,
  websiteConfig: WebsiteConfig,
  /** J6: "Import website config from event" names the source event (`?sourceEventId=`). */
  sourceEventId?: string,
): Promise<void> {
  const allFields = TEMPLATE_WEBSITE_CONFIG_FIELDS;

  const body: Record<string, unknown> = {};
  for (const key of allFields) {
    if (websiteConfig[key] !== undefined) {
      body[key] = websiteConfig[key];
    }
  }

  await apiPut<void>(
    withSourceEventId(`${TEMPLATE_WEBSITE_CONFIG_PATH}${templateId}`, sourceEventId),
    body,
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Update template website config and a specific page together
 */
export async function updateTemplateWebsiteConfigAndPage(
  templateId: string,
  websiteConfig: WebsiteConfig,
  page: Page,
): Promise<void> {
  // Full field set, same as the non-template twin — the hardcoded 15-key
  // object this replaced dropped every theme field on a combined save.
  const body: Record<string, unknown> = {};
  for (const key of TEMPLATE_WEBSITE_CONFIG_FIELDS) {
    if (websiteConfig[key] !== undefined) {
      body[key] = websiteConfig[key];
    }
  }
  body.pageId = page.pageId;
  body.websiteJson = page.websiteJson;

  await apiPut(`${TEMPLATE_PAGES_PATH}${templateId}/with-website-config`, body, {
    "Cache-Control": "no-store",
  });
}

/**
 * Get all pages for a template
 */
export async function getTemplatePages(
  templateId: string,
): Promise<Page[]> {
  const response = await apiGet<Page[]>(
    `${TEMPLATE_PAGES_PATH}${templateId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  return response ?? [];
}

/**
 * Get a template page by slug
 */
export async function getTemplatePage(
  templateId: string,
  slug: string,
): Promise<Page> {
  return await apiGet<Page>(
    `${TEMPLATE_PAGES_PATH}${templateId}/by-slug/${encodeURIComponent(slug)}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Create a standalone template page
 */
export async function createTemplatePage(
  templateId: string,
  page: Pick<Page, "pageName" | "slug" | "active" | "websiteJson">,
  /** J6: "Import pages from event" names the source event (`?sourceEventId=`). */
  sourceEventId?: string,
): Promise<string> {
  const response = await apiPost<string>(
    withSourceEventId(`${TEMPLATE_PAGES_PATH}${templateId}`, sourceEventId),
    {
      pageName: page.pageName,
      slug: page.slug,
      active: page.active,
      websiteJson: page.websiteJson ?? "",
    },
    {
      "Cache-Control": "no-store",
    },
  );
  if (!response) throw new Error("unknown");
  return response;
}

/**
 * Update a standalone template page
 */
export async function updateTemplatePage(
  templateId: string,
  pageId: string,
  page: Partial<Pick<Page, "pageName" | "slug" | "active" | "websiteJson">>,
): Promise<void> {
  await apiPut<void>(
    `${TEMPLATE_PAGES_PATH}${templateId}/${pageId}`,
    page,
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Delete a standalone template page
 */
export async function deleteTemplatePage(
  templateId: string,
  pageId: string,
): Promise<void> {
  await apiDelete<void>(`${TEMPLATE_PAGES_PATH}${templateId}/${pageId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Get a presigned S3 URL for uploading a custom website ZIP file for a template
 */
export async function getPresignedCustomWebsiteUploadUrlTemplate(
  templateId: string,
  fileName: string,
  fileSize: number,
): Promise<{ uploadUrl: string }> {
  return await apiPost<{ uploadUrl: string }>(
    `${TEMPLATE_WEBSITE_CONFIG_PATH}${templateId}/presign-custom-website-upload`,
    { fileName, fileSize },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Set up a custom website for a template from scratch: create minimal config + process ZIP + activate.
 */
export async function setupCustomWebsiteTemplate(
  templateId: string,
  fileName: string,
): Promise<void> {
  await apiPost<void>(
    `${TEMPLATE_WEBSITE_CONFIG_PATH}${templateId}/setup-custom-website`,
    { fileName },
    { "Cache-Control": "no-store" },
  );
}

/**
 * Duplicate a standalone template page
 */
export async function duplicateTemplatePage(
  templateId: string,
  pageId: string,
): Promise<string> {
  const response = await apiPost<string>(
    `${TEMPLATE_PAGES_PATH}${templateId}/${pageId}/duplicate`,
    {},
    {
      "Cache-Control": "no-store",
    },
  );
  if (!response) throw new Error("unknown");
  return response;
}

// (legacy shim block removed — duplicates earlier definitions in this file)
